import dayjs from 'dayjs';
import is from 'ramda/src/is';
import isNil from 'ramda/src/isNil';
import path from 'ramda/src/path';

import { EventManagementColumnsFiltersArrayType } from '@ge/feat-admin/models/event-management-table-cols';
import { TasksColumns } from '@ge/feat-manage/models/tasks-table-cols';
import { TableFilterTypes, DateTimeFormats, TaskDateKey, dlColumns } from '@ge/models/constants';
import { searchStr } from '@ge/shared/util/general';

const { camelize } = require('@ge/util/string-utils');

// passing in filter values by ref to build the unique values for data set
// can refactor this, but since this func is used internally not sure it matters
const filterItem = ({ item, filterDict, isTaskTable, isDistributionList, isFleetTable }) => {
  let isFiltered = false;

  for (const [key, { max, min, type, value }] of filterDict) {
    let prop = path([camelize(key?.split('.').toString())], item) || path(key?.split('-'), item);

    // if prop is complex object, we expect there to be a description field
    // can revisit this, does it make more sense to call it 'label'?
    if (is(Object, prop) && !Array.isArray(prop)) {
      prop = prop.description ?? prop.name ?? prop.value;
    }
    if (type === TableFilterTypes.DATE && isTaskTable && item[key] !== '-') {
      let temp = `${camelize(key?.split('.').toString())}Tz`;
      if (temp === 'scheduledDateTz') {
        temp = TaskDateKey.SCHEDULE_DATE_TZ;
      }
      prop = path([temp], item);
      // can revisit this logic based on the requirement clarification
      if (prop == null) {
        isFiltered = true;
        continue;
      }
    }
    if (key === 'parts-expected' || key === 'parts-consumed') {
      prop = item[key]?.count;
    }

    // Checks if an empty cell is an empty string instead of null/undefined.
    if (value && !prop?.toString()?.length) {
      isFiltered = true;
      break;
    }

    // can revisit this logic
    if (prop === null || prop === undefined) {
      isFiltered = true;
      continue;
    }
    // if prop is empty filter it
    if (prop === '' || prop === null || prop === undefined) {
      isFiltered = true;
      continue;
    }
    switch (type) {
      case TableFilterTypes.CHECKBOXES: {
        if (value?.length) {
          let hasMatch = false;
          if (
            key === TasksColumns.ASSIGNED_TECHS ||
            EventManagementColumnsFiltersArrayType.includes(key)
          ) {
            isFiltered = Array.isArray(prop)
              ? !value.some((elem) => prop.includes(elem))
              : !value.includes(prop);
          } else if (isDistributionList) {
            isFiltered = Array.isArray(prop)
              ? !value.some((elem) => prop.includes(elem))
              : !value.includes(prop);
          } else if (isFleetTable) {
            if (Array.isArray(prop)) {
              hasMatch = prop.some(p => {
                const pStr = String(p)?.trim();
                return value.some(v => String(v)?.trim() === pStr);
              });
            } else {
              const propStr = String(prop)?.trim();
              hasMatch = value.some(v => String(v)?.trim() === propStr);
            }
            isFiltered = !hasMatch;
          } else {
            isFiltered = Array.isArray(prop)
              ? !value.every((elem) => prop.includes(elem))
              : !value.includes(prop);
          }
        }
        break;
      }
      case TableFilterTypes.DATE: {
        let _prop;

        if (typeof prop === 'string' && !prop.includes('-')) {
          const timeStamp = Number(prop);
          if (!isNaN(timeStamp)) {
            _prop = dayjs(
              dayjs(timeStamp * 1000).format(DateTimeFormats.DEFAULT_DATE),
              DateTimeFormats.DEFAULT_DATE,
            );
          }
        } else {
          _prop = dayjs(
            dayjs(prop).format(DateTimeFormats.DEFAULT_DATE),
            DateTimeFormats.DEFAULT_DATE,
          );
        }

        if (!_prop.isValid()) {
          continue;
        }

        if (value) {
          const _value = dayjs(
            dayjs(value).format(DateTimeFormats.DEFAULT_DATE),
            DateTimeFormats.DEFAULT_DATE,
          );

          if (_value.isValid()) {
            isFiltered = !_prop.isSame(_value);
          }
        }

        if (max && min) {
          const _max = dayjs(
            dayjs(max).format(DateTimeFormats.DEFAULT_DATE),
            DateTimeFormats.DEFAULT_DATE,
          );
          const _min = dayjs(
            dayjs(min).format(DateTimeFormats.DEFAULT_DATE),
            DateTimeFormats.DEFAULT_DATE,
          );

          if (_max.isValid() && _min.isValid()) {
            isFiltered = !_prop.isBetween(_min, _max) && !_prop.isSame(_min) && !_prop.isSame(_max);
          }
        }

        break;
      }
      case TableFilterTypes.NUMBER: {
        const checkMinMaxAbsent = max && min ? false : true;
        if (isTaskTable) {
          prop === '-' ? (prop = 0) : prop;
        }
        const _prop = Number.parseFloat(prop);

        if (Number.isNaN(_prop)) {
          continue;
        }

        if (value && checkMinMaxAbsent) {
          const _value = Number.parseFloat(value);

          if (!Number.isNaN(_value)) {
            isFiltered = _prop?.toFixed(2) !== _value?.toFixed(2);
          }
        } else if (max && min) {
          const _max = Number.parseFloat(max);
          const _min = Number.parseFloat(min);
          if (
            !Number.isNaN(_min) &&
            !Number.isNaN(_max) &&
            Number.POSITIVE_INFINITY !== _min &&
            Number.POSITIVE_INFINITY !== _max &&
            Number.NEGATIVE_INFINITY !== _min &&
            Number.NEGATIVE_INFINITY !== _max
          ) {
            // Handle value, range
            isFiltered = _min > _prop || _prop > _max;
          } else if (!(Number.isNaN(_max) || Number.isNaN(_min))) {
            // Handle min, max filter for greater & less than
            isFiltered = _min >= _prop || _prop >= _max;
          }
        }
        break;
      }
      case TableFilterTypes.SEARCH: {
        if (isNil(value)) {
          isFiltered = false;
        } else if (Array.isArray(value)) {
          isFiltered = !value.some((_value) => searchStr(prop, _value));
        } else {
          isFiltered = !searchStr(prop, value);
        }

        break;
      }
      case TableFilterTypes.PROGRESSIVE_DROPDOWNS: {
        if (value?.length) {
          isFiltered = !value.every((sv) => Object.values(prop).includes(sv));
        }

        break;
      }
      case TableFilterTypes.PROGRESSIVE_MULTI_SELECT: {
        if (value?.length) {
          isFiltered = !value.every((sv, i) => sv.includes(prop[i]));
        }

        break;
      }
    }

    if (isFiltered) {
      break;
    }
  }

  return isFiltered;
};

const hasPrevFilter = ({ item, filterDict = [], filterSequence = [], isFleetTable = false }) => {
  const isMatched = filterSequence.every((prevFilterKey) => {
    let prevProp = path([prevFilterKey], item) || path(prevFilterKey?.split('-'), item);
    
    if (is(Object, prevProp) && !Array.isArray(prevProp)) {
      prevProp = prevProp.description ?? prevProp.name ?? prevProp.value;
    }
    const filterValue = filterDict.find(([key]) => key === prevFilterKey)?.[1];
    if (!filterValue) return true;

    if (filterValue.value?.length) {
      if (Array.isArray(prevProp) && isFleetTable) {
        return prevProp.some(p => {
          const pStr = String(p)?.trim();
          return filterValue?.value?.some(v => String(v)?.trim() === pStr);
        });
      }
      if (filterValue.type === TableFilterTypes.PROGRESSIVE_MULTI_SELECT && Array.isArray(prevProp)) {
        return prevProp.every((val, i) => filterValue.value[i].includes(val));
      }

      if (filterValue.type === TableFilterTypes.NUMBER) {
        return filterValue.value.includes(String(prevProp));
      }

      if (filterValue.type === TableFilterTypes.DATE) {
        const _prop = dayjs(
          dayjs(prevProp).format(DateTimeFormats.DEFAULT_DATE),
          DateTimeFormats.DEFAULT_DATE,
        );
        const _propVal = dayjs(
          dayjs(filterValue.value).format(DateTimeFormats.DEFAULT_DATE),
          DateTimeFormats.DEFAULT_DATE,
        );
        return _prop.isSame(_propVal);
      }

      const propStr = String(prevProp)?.trim();
      return filterValue?.value?.some(v => String(v)?.trim() === propStr);
    }

    if (filterValue.type === TableFilterTypes.DATE) {
      const { min, max, value } = filterValue;
      if (max && min && prevProp) {
        const _prop = dayjs(
          dayjs(prevProp).format(DateTimeFormats.DEFAULT_DATE),
          DateTimeFormats.DEFAULT_DATE,
        );
        const _max = dayjs(
          dayjs(max).format(DateTimeFormats.DEFAULT_DATE),
          DateTimeFormats.DEFAULT_DATE,
        );
        const _min = dayjs(
          dayjs(min).format(DateTimeFormats.DEFAULT_DATE),
          DateTimeFormats.DEFAULT_DATE,
        );
        if (_max.isValid() && _min.isValid()) {
          return _prop.isBetween(_min, _max) || _prop.isSame(_min) || _prop.isSame(_max);
        }
      }
      if (value && prevProp) {
        const _prop = dayjs(
          dayjs(prevProp).format(DateTimeFormats.DEFAULT_DATE),
          DateTimeFormats.DEFAULT_DATE,
        );
        const _val = dayjs(
          dayjs(value).format(DateTimeFormats.DEFAULT_DATE),
          DateTimeFormats.DEFAULT_DATE,
        );
        if (_val.isValid()) {
          return _prop.isSame(_val);
        }
      }
      return false;
    }

    if (filterValue.min && filterValue.max) {
      const propNum = Number(prevProp);
      return filterValue.min < propNum && propNum < filterValue.max;
    }

    return true;
  });

  return isMatched;
};
// filter item
const filterItemUp = ({ item, filterDict, filterValues, isTaskTable, filterSequence }) => {
  //let isFiltered = false;

  for (const [key, { type, value }] of filterDict) {
    let prop = path([camelize(key?.split('.').toString())], item) || path(key?.split('-'), item);

    // if prop is complex object, we expect there to be a description field
    // can revisit this, does it make more sense to call it 'label'?
    if (is(Object, prop)) {
      prop = prop.description ?? prop.name ?? prop.value;
    }
    if (type === TableFilterTypes.DATE && isTaskTable && item[key] !== '-') {
      let temp = `${camelize(key?.split('.').toString())}Tz`;
      if (temp === 'scheduledDateTz') {
        temp = TaskDateKey.SCHEDULE_DATE_TZ;
      }
      prop = path([temp], item);
    }
    if (key === 'parts-expected' || key === 'parts-consumed') {
      prop = item[key]?.count;
    }

    // Checks if an empty cell is an empty string instead of null/undefined.
    if (value && !prop?.toString()?.length) {
      //isFiltered = true;
      break;
    }

    // can revisit this logic
    if (prop == null || prop == '-') {
      continue;
    }
    // Checks filter sequence.
    if (type === TableFilterTypes.CHECKBOXES && filterSequence?.length) {
      const prevFiltersSequence =
        filterSequence.indexOf(key) === -1
          ? filterSequence
          : filterSequence.slice(0, filterSequence.indexOf(key));

      if (prevFiltersSequence?.length) {
        const isMatchedPrevFilters = hasPrevFilter({
          item,
          filterDict,
          filterSequence: prevFiltersSequence,
        });

        if (!isMatchedPrevFilters) continue;
      }
    }
    // add value to static filter options if exists
    filterValues[key]?.add(prop);
  }
};

const SitefilterItemUp = ({ item, filterDict, filterValues, isTaskTable, filterSequence, isFleetTable }) => {
  for (const [key, { type, value }] of filterDict) {
      let prop = path([camelize(key?.split('.').toString())], item) || path(key?.split('-'), item);

      if (is(Object, prop) && !Array.isArray(prop)) {
          prop = prop.description ?? prop.name ?? prop.value;
      }
      if (type === TableFilterTypes.DATE && isTaskTable && item[key] !== '-') {
          let temp = `${camelize(key?.split('.').toString())}Tz`;
          if (temp === 'scheduledDateTz') {
              temp = TaskDateKey.SCHEDULE_DATE_TZ;
          }
          prop = path([temp], item);
      }
      if (key === 'parts-expected' || key === 'parts-consumed') {
          prop = item[key]?.count;
      }
      if (value && !prop?.toString()?.length) {
          continue;
      }
      if (prop == null || prop === '-') {
          continue;
      }
      const prevFiltersSequence =
          filterSequence.indexOf(key) === -1
              ? filterSequence
              : filterSequence.slice(0, filterSequence.indexOf(key));

      let canBeAdded = true;
      if (prevFiltersSequence?.length) {
          canBeAdded = hasPrevFilter({
              item,
              filterDict,
            filterSequence: prevFiltersSequence,
            isFleetTable,
          });
      }
      if (canBeAdded && filterValues[key]) {
          if (Array.isArray(prop)) {
              prop.forEach(p => {
                  if (p != null) {
                      filterValues[key].add(String(p));
                  }
              });
          } else {
              filterValues[key].add(String(prop));
          }
      }
  }
};


// for in-memory filtering
export const getDataFilter = ({ data, filters, isTaskTable, isDistributionList, isFleetTable }) => {
  if (!data?.length) {
    // is this the best default?
    return {};
  }

  const filterDict = Object.entries(filters ?? {});

  //.filter(([, { value }]) => value !== undefined);

  // right now building filter values dynamically from currently visible dataset
  // if that requirement changes, we can look at doing this only when the underlying dataset
  // changes, so hopefully once per session or after a refresh

  const filteredData = filterDict.length
    ? data.filter((item) => !filterItem({ item, filterDict, isTaskTable, isDistributionList, isFleetTable }))
    : data;
  return {
    data: filteredData,
  };
};

export const getDataFilterUp = ({ data, filters, isTaskTable, filterSequence, isFleetTable }) => {
  if (!data?.length) {
    // is this the best default?
    return {};
  }

  const filterDict = Object.entries(filters ?? {});

  const getFilterValues = (filterType) =>
    filterDict
      .filter(([, { type }]) => type === filterType)
      .reduce((values, [key]) => {
        values[key] = new Set();
        return values;
      }, {});

  // right now building filter values dynamically from currently visible dataset
  // if that requirement changes, we can look at doing this only when the underlying dataset
  // changes, so hopefully once per session or after a refresh
  const filterValues = {
    ...getFilterValues(TableFilterTypes.PROGRESSIVE_MULTI_SELECT),
    ...getFilterValues(TableFilterTypes.PROGRESSIVE_DROPDOWNS),
    ...getFilterValues(TableFilterTypes.CHECKBOXES),
  };

  const fiterItem = isFleetTable ? SitefilterItemUp : filterItemUp;
  data.filter(
    (item) => !fiterItem({ item, filterDict, filterValues, isTaskTable, filterSequence, isFleetTable }),
  );

  return {
    filterValues: Object.entries(filterValues).reduce((_filterValues, [key, value]) => {
      _filterValues[key] = Array.from(value).sort((a, b) => {
        return String(a).localeCompare(b, undefined, { numeric: true, caseFirst: 'upper' });
      });
      return _filterValues;
    }, {}),
  };
};


export const getFilterValuesForDL = (data = [], filters = {}, primarySelectionField) => {
  const filterValues = {
    name: new Set(),
    site: new Set(),
    asset: new Set(),
    desc: new Set()
  };

  const filterDict = Object.entries(filters);

  if (!data || data.length === 0) return {
    name: [],
    site: [],
    asset: [],
    desc: []
  };

  data.forEach(item => {
    let match = true;
    filterDict.forEach(([field, filterOptions]) => {
      if (filterOptions.type === TableFilterTypes.CHECKBOXES && Array.isArray(filterOptions.value)) {
        const selectedValues = filterOptions.value;
        if (field === dlColumns.SITE && item.site?.value) {
          match = match && item.site.value.some(site => selectedValues.includes(site));
        }
        if (field === dlColumns.ASSET && item.asset?.value) {
          match = match && item.asset.value.some(asset => selectedValues.includes(asset));
        }
        if (field === dlColumns.NAME) {
          match = match && selectedValues.includes(item.name);
        }
        if (field === dlColumns.DESC) {
          match = match && selectedValues.includes(item.desc);
        }
      }
    });

    if (match) {
      filterValues.name.add(item.name);
      item.site?.value?.forEach(site => filterValues.site.add(site));
      item.asset?.value?.forEach(asset => filterValues.asset.add(asset));
      filterValues.desc.add(item.desc);
    }

    if (primarySelectionField) {
      if (primarySelectionField === dlColumns.SITE || primarySelectionField === dlColumns.ASSET) {
        item[primarySelectionField]?.value?.forEach(item => filterValues[primarySelectionField].add(item));
      } else {
        filterValues[primarySelectionField].add(item[primarySelectionField]);
      }
    }
  });

  return Object.keys(filterValues).reduce((acc, key) => {
    acc[key] = Array.from(filterValues[key]);
    return acc;
  }, {});
};
